import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";

export default function DcodeAlternative({ t }) {
  const css = `
.alt-page { max-width: 820px; margin: 0 auto; padding: 3rem 2rem 5rem; flex: 1; }
.alt-eyebrow { font-size: 11px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.14em; color: ${t.textMuted}; font-family: 'JetBrains Mono', monospace; margin-bottom: 10px; }
.alt-h1 { font-size: clamp(26px, 4vw, 42px); font-weight: 800; letter-spacing: -0.03em; color: ${t.textPrimary}; margin-bottom: 16px; line-height: 1.1; }
.alt-h1 em { font-style: normal; background: linear-gradient(135deg, #F05A41 0%, #D8A056 100%); -webkit-background-clip: text; -webkit-text-fill-color: transparent; background-clip: text; }
.alt-lead { font-size: 15px; color: ${t.textSecondary}; line-height: 1.7; max-width: 640px; margin-bottom: 2rem; }
.alt-table-wrap { overflow-x: auto; margin-bottom: 2.5rem; border: 1px solid ${t.border}; border-radius: 14px; }
.alt-table { width: 100%; border-collapse: collapse; font-size: 13px; }
.alt-table th { padding: 13px 18px; text-align: left; font-size: 11px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.10em; color: ${t.textMuted}; font-family: 'JetBrains Mono', monospace; border-bottom: 1px solid ${t.border}; background: ${t.bgPanelHead}; }
.alt-table td { padding: 14px 18px; border-bottom: 1px solid ${t.border}; color: ${t.textSecondary}; vertical-align: top; line-height: 1.5; }
.alt-table tr:last-child td { border-bottom: none; }
.alt-table td:first-child { font-weight: 600; color: ${t.textPrimary}; white-space: nowrap; }
.alt-yes { color: #22c55e; font-weight: 700; }
.alt-no  { color: #ef4444; font-weight: 700; }
.alt-part { color: #f59e0b; font-weight: 700; }
.alt-section { margin-bottom: 2.5rem; }
.alt-h2 { font-size: 22px; font-weight: 800; color: ${t.textPrimary}; letter-spacing: -0.03em; margin-bottom: 12px; }
.alt-p { font-size: 14px; color: ${t.textSecondary}; line-height: 1.7; margin-bottom: 12px; }
.alt-ul { margin: 8px 0 16px; padding-left: 0; list-style: none; display: flex; flex-direction: column; gap: 6px; }
.alt-ul li { font-size: 13px; color: ${t.textSecondary}; padding-left: 20px; position: relative; line-height: 1.6; }
.alt-ul li::before { content: '→'; position: absolute; left: 0; color: ${t.textAccent}; font-weight: 700; }
.alt-faq-item { border: 1px solid ${t.border}; border-radius: 12px; margin-bottom: 10px; overflow: hidden; }
.alt-faq-item summary { padding: 14px 18px; font-size: 14px; font-weight: 700; color: ${t.textPrimary}; cursor: pointer; list-style: none; display: flex; align-items: center; justify-content: space-between; }
.alt-faq-item summary::after { content: '+'; font-size: 18px; color: ${t.textAccent}; flex-shrink: 0; }
.alt-faq-item[open] summary::after { content: '−'; }
.alt-faq-body { padding: 0 18px 14px; font-size: 13px; color: ${t.textSecondary}; line-height: 1.75; }
.alt-cta-row { display: grid; grid-template-columns: 1fr 1fr; gap: 12px; margin-bottom: 2rem; }
.alt-cta { background: linear-gradient(135deg, rgba(240,90,65,0.08), rgba(216,160,86,0.08)); border: 1px solid rgba(240,90,65,0.20); border-radius: 16px; padding: 24px; text-align: center; }
.alt-cta-title { font-size: 17px; font-weight: 800; color: ${t.textPrimary}; margin-bottom: 8px; }
.alt-cta-sub { font-size: 12px; color: ${t.textSecondary}; margin-bottom: 16px; }
.alt-footer-nav { display: flex; gap: 20px; flex-wrap: wrap; padding-top: 24px; border-top: 1px solid ${t.border}; }
.alt-footer-nav a { font-size: 13px; color: ${t.textAccent}; text-decoration: none; font-weight: 600; }
@media (max-width: 600px) { .alt-page { padding: 2rem 1.25rem 4rem; } .alt-cta-row { grid-template-columns: 1fr; } }
`;

  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": [
      { "@type": "Question", "name": "Is miniDevToolkit a good alternative to dcode.fr?", "acceptedAnswer": { "@type": "Answer", "text": "Yes. miniDevToolkit covers the same everyday encoding and developer utility tasks as dcode.fr — Base64, URL encoding, HTML entities, Hex, Unicode, MD5/SHA-256 hashing — and adds a JSON Toolkit, file converter, UUID generator, fake data generator, regex tester, and more. Both are free and client-side, but miniDevToolkit bundles more tools in one place." } },
      { "@type": "Question", "name": "Is dcode.fr ad-free?", "acceptedAnswer": { "@type": "Answer", "text": "dcode.fr has ads on many tool pages. miniDevToolkit also uses ads to remain free, but they are non-intrusive display ads served by Google AdSense. Neither site requires a subscription to use any tool." } },
      { "@type": "Question", "name": "Does miniDevToolkit have the same hash tools as dcode.fr?", "acceptedAnswer": { "@type": "Answer", "text": "Yes. miniDevToolkit's Hash & Security toolkit at minidevtoolkit.com/hash-generator includes MD5, SHA-256, and SHA-512 hashing for text and files. The file hash tool computes hashes locally via the Web Crypto API — your files are never uploaded." } },
    ],
  };

  return (
    <>
      <Helmet>
        <title>Free dcode.fr Alternative — Clean Dev Utilities | miniDevToolkit</title>
        <meta name="description" content="miniDevToolkit is a free dcode.fr alternative: encoding, hash, and text utilities in one privacy-first toolkit. No sign-up, no uploads, browser-based." />
        <link rel="canonical" href="https://minidevtoolkit.com/alternatives/dcode" />
        <meta property="og:title" content="Free dcode.fr Alternative | miniDevToolkit" />
        <meta property="og:description" content="miniDevToolkit is a free dcode.fr alternative: encoding, hash, and text utilities in one privacy-first toolkit. No sign-up, no uploads, browser-based." />
        <meta property="og:url" content="https://minidevtoolkit.com/alternatives/dcode" />
        <meta name="twitter:card" content="summary_large_image" />
        <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>
      </Helmet>

      <style>{css}</style>

      <main className="alt-page">
        <div className="alt-eyebrow">Alternatives · dcode.fr</div>
        <h1 className="alt-h1">
          Free dcode.fr Alternative —<br />
          <em>Clean, Privacy-First Dev Utilities</em>
        </h1>
        <p className="alt-lead">
          dcode.fr is a useful collection of encoding and cipher tools. miniDevToolkit covers the same everyday encoding,
          hashing, and text utility tasks — Base64, URL encoding, HTML entities, Hex, Unicode, MD5, SHA-256 — and
          goes further with a JSON toolkit, UUID generator, file converter, regex tester, and fake data generator,
          all in a single clean interface.
        </p>

        <section className="alt-section" aria-labelledby="compare-heading">
          <h2 className="alt-h2" id="compare-heading">Feature Comparison</h2>
          <div className="alt-table-wrap">
            <table className="alt-table">
              <thead>
                <tr>
                  <th>Feature</th>
                  <th>dcode.fr</th>
                  <th>miniDevToolkit</th>
                </tr>
              </thead>
              <tbody>
                <tr><td>Base64 Encode / Decode</td><td><span className="alt-yes">✓ Yes</span></td><td><span className="alt-yes">✓ Yes</span> — + image preview</td></tr>
                <tr><td>URL Encode / Decode</td><td><span className="alt-yes">✓ Yes</span></td><td><span className="alt-yes">✓ Yes</span></td></tr>
                <tr><td>HTML Entity Encode / Decode</td><td><span className="alt-yes">✓ Yes</span></td><td><span className="alt-yes">✓ Yes</span></td></tr>
                <tr><td>Hex Encode / Decode</td><td><span className="alt-yes">✓ Yes</span></td><td><span className="alt-yes">✓ Yes</span></td></tr>
                <tr><td>MD5 / SHA-256 Hash</td><td><span className="alt-yes">✓ Yes</span></td><td><span className="alt-yes">✓ Yes</span> — + SHA-512, file hash</td></tr>
                <tr><td>JWT Decode</td><td><span className="alt-no">✗ No</span></td><td><span className="alt-yes">✓ Yes</span> — + expiry check</td></tr>
                <tr><td>JSON Toolkit (format / diff / convert)</td><td><span className="alt-no">✗ No</span></td><td><span className="alt-yes">✓ Yes</span> — 7 JSON tools</td></tr>
                <tr><td>UUID Generator</td><td><span className="alt-no">✗ No</span></td><td><span className="alt-yes">✓ Yes</span> — v4/v1/nil, bulk</td></tr>
                <tr><td>File Hash (no upload)</td><td><span className="alt-no">✗ No</span></td><td><span className="alt-yes">✓ Yes</span> — Web Crypto API</td></tr>
                <tr><td>Regex Tester</td><td><span className="alt-no">✗ No</span></td><td><span className="alt-yes">✓ Yes</span> — live highlighting</td></tr>
                <tr><td>File Converter (PDF / DOCX)</td><td><span className="alt-no">✗ No</span></td><td><span className="alt-yes">✓ Yes</span></td></tr>
                <tr><td>Cipher / Code tools</td><td><span className="alt-yes">✓ Extensive</span></td><td><span className="alt-part">⚠ Morse only</span></td></tr>
                <tr><td>100% Client-side</td><td><span className="alt-yes">✓ Yes</span></td><td><span className="alt-yes">✓ Yes</span></td></tr>
                <tr><td>Sign-up required</td><td><span className="alt-yes">Not required</span></td><td><span className="alt-yes">Not required</span></td></tr>
              </tbody>
            </table>
          </div>
        </section>

        <section className="alt-section" aria-labelledby="when-heading">
          <h2 className="alt-h2" id="when-heading">When miniDevToolkit is the better choice</h2>
          <p className="alt-p">dcode.fr excels at cipher and code-breaking tools (Caesar cipher, Vigenère, Braille, etc.). If you need those, dcode.fr is the right site.</p>
          <p className="alt-p">Choose miniDevToolkit when you need:</p>
          <ul className="alt-ul">
            <li>A JWT decoder with expiry checking in a single tab</li>
            <li>File hashing (MD5, SHA-256, SHA-512) with no server upload</li>
            <li>JSON formatting, validation, diff, and conversion (CSV/XML/YAML)</li>
            <li>UUID generation, fake data, or a regex tester</li>
            <li>All these tools in one bookmarked URL</li>
          </ul>
        </section>

        <section className="alt-section" aria-labelledby="faq-heading">
          <h2 className="alt-h2" id="faq-heading">Frequently Asked Questions</h2>
          {[
            { q: "Is miniDevToolkit a good alternative to dcode.fr?", a: "Yes. miniDevToolkit covers the same everyday encoding and developer utility tasks as dcode.fr — Base64, URL encoding, HTML entities, Hex, Unicode, MD5/SHA-256 hashing — and adds a JSON Toolkit, file converter, UUID generator, fake data generator, regex tester, and more. Both are free and client-side." },
            { q: "Does miniDevToolkit have the same hash tools as dcode.fr?", a: "Yes. miniDevToolkit's Hash & Security toolkit includes MD5, SHA-256, and SHA-512 hashing for text and files. The file hash tool computes hashes locally via the Web Crypto API — your files are never uploaded." },
            { q: "Is miniDevToolkit free like dcode.fr?", a: "Yes. All tools on miniDevToolkit are completely free with no account or subscription required. The site is supported by non-intrusive display ads." },
          ].map((item, i) => (
            <details key={i} className="alt-faq-item">
              <summary>{item.q}</summary>
              <div className="alt-faq-body">{item.a}</div>
            </details>
          ))}
        </section>

        <div className="alt-cta-row">
          <div className="alt-cta">
            <div className="alt-cta-title">Encoding Toolkit</div>
            <div className="alt-cta-sub">14 encoders in one tab — Base64, JWT, URL, Hex, Unicode, Morse</div>
            <Link to="/encoding-toolkit" className="btn-primary" style={{ display: "inline-block", padding: "10px 22px", fontSize: 13, textDecoration: "none" }}>
              Open Encoding Toolkit →
            </Link>
          </div>
          <div className="alt-cta">
            <div className="alt-cta-title">Hash & Security</div>
            <div className="alt-cta-sub">MD5, SHA-256, SHA-512 for text and files. No upload.</div>
            <Link to="/hash-generator" className="btn-primary" style={{ display: "inline-block", padding: "10px 22px", fontSize: 13, textDecoration: "none" }}>
              Open Hash Generator →
            </Link>
          </div>
        </div>

        <nav className="alt-footer-nav" aria-label="Related pages">
          <Link to="/">← Home</Link>
          <Link to="/encoding-toolkit">Encoding Toolkit</Link>
          <Link to="/hash-generator">Hash Generator</Link>
          <Link to="/alternatives/jsonformatter">vs JSONFormatter</Link>
          <Link to="/alternatives/cyberchef">vs CyberChef</Link>
        </nav>
      </main>
    </>
  );
}
